import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, InputAdornment, Typography } from '@mui/material';
import { PKDModal } from 'components/Modal';
import { PKDFilledButton } from 'components/FilledButton';
import { FormRow } from 'components/common/FormWrapper/styles';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { Container } from 'shared-components/Container';
import { openSnackbar } from 'store/application';
import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import { TextInput } from 'components/PKDTextField';
import {
  RemoveRedEyeOutlined,
  VisibilityOffOutlined,
} from '@mui/icons-material';
import { passwordStrengthRegExp } from 'utils/regexp';
import { useChangeUserPasswordMutation } from 'store/api/pkd/endpoints/user';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';

const initialFormValues = {
  confirmPassword : '',
  newPassword     : '',
  oldPassword     : '',
};

export const ChangePassword = () => {

  const theme = useTheme();
  const dispatch = useDispatch();
  const { isCurrentPersonLoading, userId } =
    useGetPersonAndAttributes();
  const [changeUserPassword, { isLoading: isUpdating }] = useChangeUserPasswordMutation();

  const passwordValidationMessage =
    'Please make sure your password is at least 8 characters long and has one number, one lowercase letter, one uppercase letter, and one special character (such as # * @ ^)';
  const passwordLengthMessage =
    'Your password must be at least 8 characters long';

  const {
    formState: { errors, isValid },
    control,
    watch,
    getValues,
    reset,
  } = useForm({
    mode          : 'onChange',
    defaultValues : initialFormValues,
  });

  const { newPassword, oldPassword, confirmPassword } = getValues();

  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const toggleChangePassword = () => setIsChangePasswordOpen((prev) => !prev);
  const handleCloseModal = () => {
    reset({
      confirmPassword : '',
      newPassword     : '',
      oldPassword     : '',
    });
    toggleChangePassword();
  };

  const handleSubmit = async () => {
    const body = {
      old_password          : oldPassword,
      password              : newPassword,
      password_confirmation : confirmPassword,
    };

    const { error } = await changeUserPassword({
      userId,
      body
    });
    if (error) {
      return dispatch(
        openSnackbar({
          type    : 'error',
          message : 'Could not change your password. Please try again',
        })
      );
    }
    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Password changed successfully',
      })
    );
    reset({
      oldPassword     : '',
      confirmPassword : '',
      newPassword     : '',
    });
    toggleChangePassword();
  };

  const [passwordFieldTypes, setPasswordFieldTypes] = useState({
    oldPassword     : true,
    newPassword     : true,
    confirmPassword : true,
  });

  const toggleFieldType = (fieldName) => {
    setPasswordFieldTypes((prev) => ({
      ...prev,
      [fieldName] : !prev[fieldName],
    }));
  };
 
  return (
    <>
      <PKDModal
        isOpen={isChangePasswordOpen}
        onClose={handleCloseModal}
        title='Change password'
        maxWidth={{ xl: '800px' }}>
        {
          isCurrentPersonLoading
            ? <Box
              display='flex' alignItems='center' justifyItems='center'>
              <CircularProgress
                data-testid='loading-spinner' sx={{ marginLeft: 1 }} color='inherit' size={20}
              />
            </Box>
            : <Box
              margin='24px 85px'
              display='flex'
              flexDirection='column'
              justifyContent='center'>
              <FormRow
                marginTop='8px' cols='2' gridGap='16px'>
                <Box>
                  <Controller
                    control={control}
                    name='oldPassword'
                    rules={{
                      required : {
                        value   : true,
                        message : 'Please enter your old password',
                      }
                    }}
                    render={({ field: { onChange } }) => (
                      <ErrorWrapper
                        message={errors.oldPassword}>
                        <TextInput
                          value={watch('oldPassword')}
                          onChange={onChange}
                          label='Old Password'
                          fullWidth
                          type={
                            passwordFieldTypes.oldPassword ? 'password' : 'text'
                          }
                          error={!!errors?.oldPassword?.message}
                          InputProps={{
                            endAdornment : (
                              <InputAdornment
                                position='end'
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleFieldType('oldPassword')}>
                                {passwordFieldTypes.oldPassword
                                  ? (
                                    <RemoveRedEyeOutlined />
                                  )
                                  : (
                                    <VisibilityOffOutlined />
                                  )}
                              </InputAdornment>
                            ),
                          }}
                          hasErrors={
                            errors.oldPassword && {
                              type    : 'custom',
                              message : errors.oldPassword.message,
                            }
                          }
                        />
                      </ErrorWrapper>
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    control={control}
                    name='newPassword'
                    rules={{
                      required : {
                        value   : true,
                        message : 'Please enter your new password',
                      },
                      minLength : {
                        value   : 8,
                        message : passwordLengthMessage,
                      },
                      pattern : {
                        value   : passwordStrengthRegExp,
                        message : passwordValidationMessage,
                      },
                    }}
                    render={({ field: { onChange } }) => (
                      <ErrorWrapper
                        message={errors.newPassword}>
                        <TextInput
                          value={watch('newPassword')}
                          onChange={onChange}
                          label='New Password'
                          fullWidth
                          type={
                            passwordFieldTypes.newPassword ? 'password' : 'text'
                          }
                          error={!!errors?.newPassword?.message}
                          InputProps={{
                            endAdornment : (
                              <InputAdornment
                                position='end'
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleFieldType('newPassword')}>
                                {passwordFieldTypes.newPassword
                                  ? (
                                    <RemoveRedEyeOutlined />
                                  )
                                  : (
                                    <VisibilityOffOutlined />
                                  )}
                              </InputAdornment>
                            ),
                          }}
                          hasErrors={
                            errors.newPassword && {
                              type    : 'custom',
                              message : errors.newPassword.message,
                            }
                          }
                        />
                      </ErrorWrapper>
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    control={control}
                    name='confirmPassword'
                    rules={{
                      required : {
                        value   : true,
                        message : 'Please enter your new password',
                      },
                      minLength : {
                        value   : 8,
                        message : passwordLengthMessage,
                      },
                      validate : (value, { newPassword }) => {
                        return (
                          value === newPassword || 'Your passwords do not match'
                        );
                      },
                      pattern : {
                        value   : passwordStrengthRegExp,
                        message : passwordValidationMessage,
                      },
                    }}
                    render={({ field: { onChange } }) => (
                      <ErrorWrapper
                        message={errors.confirmPassword}>
                        <TextInput
                          value={watch('confirmPassword')}
                          onChange={onChange}
                          label='Confirm Password'
                          fullWidth
                          type={
                            passwordFieldTypes.confirmPassword ? 'password' : 'text'
                          }
                          InputProps={{
                            endAdornment : (
                              <InputAdornment
                                position='end'
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleFieldType('confirmPassword')}>
                                {passwordFieldTypes.confirmPassword
                                  ? (
                                    <RemoveRedEyeOutlined />
                                  )
                                  : (
                                    <VisibilityOffOutlined />
                                  )}
                              </InputAdornment>
                            ),
                          }}
                          error={!!errors?.confirmPassword?.message}
                          hasErrors={
                            errors.confirmPassword && {
                              type    : 'custom',
                              message : errors.confirmPassword.message,
                            }
                          }
                        />
                      </ErrorWrapper>
                    )}
                  />
                </Box>
              </FormRow>
              <Box
                mt={2} gap={2} display='flex' justifyContent='center'>
                <PKDFilledButton
                  sx={{
                    backgroundColor : theme.palette.primary.blue1,
                    color           : theme.palette.primary.white,
                    width           : '180px',
                  }}
                  onClick={handleSubmit}
                  disabled={!isValid || isUpdating}
                  isLoading={isUpdating}>
              Confirm
                </PKDFilledButton>
                <PKDFilledButton
                  sx={{
                    backgroundColor : theme.palette.primary.grey3,
                    color           : theme.palette.primary.gray7,
                    width           : '180px',
                  }}
                  onClick={handleCloseModal}>
              Cancel
                </PKDFilledButton>
              </Box>
            </Box>
        }
      </PKDModal>
      <Container
        maxWidth={{ xl: '800px' }} mb={4}>
        <Typography
          fontFamily='Montserrat'
          fontWeight='700'
          variant='h6'
          mb={4}>
          Password
        </Typography>
        <OutlineButton
          width='100%'
          fontFamily='Montserrat'
          color={theme.palette.primary.blue1}
          borderColor={theme.palette.primary.blue1}
          onClick={toggleChangePassword}
          text='Change password'
        />
      </Container>
    </>
  );
};
